/**
 * Maintenance Banner Time Utilities
 * 
 * Handles server time comparison and maintenance window detection logic
 * following the pattern from servicing-login
 */

/**
 * Parse date string to timestamp
 * Supports formats like "2026-01-19 1:00 AM" or ISO strings
 * IMPORTANT: All dates from API are in EST/EDT timezone
 * Safari-compatible: Converts to ISO 8601 format
 */
export function parseDateString(dateStr: string | null): number | null {
  if (!dateStr) return null;
  
  try {
    // If date already has timezone (ISO format with Z or offset), use it directly
    if (dateStr.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
      const date = new Date(dateStr);
      return date.getTime();
    }
    
    // Otherwise, date is in EST timezone format like "2026-01-19 1:00 AM"
    // Safari is strict about date parsing, so we need to convert to ISO 8601 format
    let cleanedDateStr = dateStr.trim();
    
    // Remove EST/EDT timezone abbreviations if present (Safari doesn't support them)
    // Use simple pattern without backtracking risks
    cleanedDateStr = cleanedDateStr.replace(/ (?:EST|EDT)$/i, '');
    
    // Parse the date format: "2026-01-19 1:00 AM"
    // Convert to ISO 8601 format with timezone offset
    // Use limited repetition to avoid backtracking issues
    const datePattern = /^(\d{4})-(\d{2})-(\d{2}) (\d{1,2}):(\d{2})(?: (AM|PM))?$/i;
    const match = datePattern.exec(cleanedDateStr);
    
    if (match) {
      const [, year, month, day, hourStr, minute, meridiem] = match;
      let hour = Number.parseInt(hourStr, 10);
      
      // Handle 12-hour format
      if (meridiem) {
        const isPM = meridiem.toUpperCase() === 'PM';
        if (isPM && hour !== 12) {
          hour += 12;
        } else if (!isPM && hour === 12) {
          hour = 0;
        }
      }
      
      // Create ISO 8601 format with EST offset (-05:00)
      // Note: This assumes EST. For EDT (-04:00), check the date/month if needed
      const isoDateStr = `${year}-${month}-${day}T${hour.toString().padStart(2, '0')}:${minute}:00-05:00`;
      const date = new Date(isoDateStr);
      
      if (Number.isNaN(date.getTime())) {
        console.error('[MaintenanceTime] Invalid parsed date:', isoDateStr);
        return null;
      }
      
      return date.getTime();
    }
    
    // Fallback: Try direct parsing with offset appended
    const dateWithOffset = cleanedDateStr + ' GMT-0500';
    const fallbackDate = new Date(dateWithOffset);
    
    if (Number.isNaN(fallbackDate.getTime())) {
      console.error('[MaintenanceTime] Invalid date:', dateStr);
      return null;
    }
    
    return fallbackDate.getTime();
  } catch (error) {
    console.error('[MaintenanceTime] Failed to parse date:', dateStr, error);
    return null;
  }
}

/**
 * Check if current time is within maintenance window
 * 
 * @param serverTime - Server timestamp in milliseconds
 * @param startDateTime - Maintenance start time string
 * @param endDateTime - Maintenance end time string
 * @returns Object with window status flags
 */
export function checkMaintenanceWindow(
  serverTime: number,
  startDateTime: string | null,
  endDateTime: string | null
): {
  isInMaintenanceWindow: boolean;
  isInOutageWindow: boolean;
  shouldShowBanner: boolean;
} {
  // Validate server time is a reasonable number
  if (!serverTime || Number.isNaN(serverTime) || serverTime <= 0) {
    console.warn('[MaintenanceTime] Invalid server time:', serverTime);
    return {
      isInMaintenanceWindow: false,
      isInOutageWindow: false,
      shouldShowBanner: false,
    };
  }

  const startTime = parseDateString(startDateTime);
  const endTime = parseDateString(endDateTime);
  
  // If we can't parse times, don't show anything
  if (startTime === null || endTime === null) {
    return {
      isInMaintenanceWindow: false,
      isInOutageWindow: false,
      shouldShowBanner: false,
    };
  }
  
  // Additional validation: ensure start < end
  if (startTime >= endTime) {
    console.warn('[MaintenanceTime] Invalid time range: start >= end', { startTime, endTime });
    return {
      isInMaintenanceWindow: false,
      isInOutageWindow: false,
      shouldShowBanner: false,
    };
  }
  
  // Current time is in outage window if between start and end
  const isInOutageWindow = serverTime >= startTime && serverTime <= endTime;
  
  // Calculate banner start time (e.g., 2 hours before maintenance)
  // This matches servicing-login pattern where banner shows before actual maintenance
  const bannerLeadTime = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  const bannerStartTime = startTime - bannerLeadTime;
  
  // We're in maintenance banner window if between banner start and maintenance start
  const isInMaintenanceWindow = serverTime >= bannerStartTime && serverTime < startTime;
  
  // Show banner if we're either in maintenance window OR outage window
  const shouldShowBanner = isInMaintenanceWindow || isInOutageWindow;
  
  return {
    isInMaintenanceWindow,
    isInOutageWindow,
    shouldShowBanner,
  };
}

/**
 * Format date for display
 */
export function formatMaintenanceDate(dateStr: string | null): string {
  if (!dateStr) return '';
  
  try {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    });
  } catch (error) {
    console.error('[MaintenanceTime] Failed to format date:', dateStr, error);
    return dateStr;
  }
}
